import { Inject, Injectable, ViewContainerRef } from "@angular/core";
import { Graph } from "@maxgraph/core";
import { DwdmDiagramAbstractConverterOscs } from "../../diagram/converter/dwdm-diagram-abstract-converter-osc";
import { OscsConverterData } from "../../diagram/converter/dwdm-diagram-converter.models";
import { CustomCell, OscElement, OscPositionMap } from "../../diagram/models/diagram.models";
import { PopoverDataService } from "../../diagram/popover/popover-data.service";
import { PopoverVisibilityManagerService } from "../../diagram/popover/popover-visibility-manager.service";
import {
    DwdmCircuitDiagnosticData,
    LINKS_STATUS_TRANSLATION_MAP,
    LinkStatus,
    PortDiagnosticData,
    TransceiverDiagnosticData
} from "../../dwdm-circuit.model";
import { OscManagementData } from "../../wizard/steps/6-osc-management/osc-management-step.model";
import { AlarmSeverity, DiagramCardData, DiagramCardInformation } from "@nms-angular-toolkit/nms-diagram-card";
import { CELSIUS, DECIBEL_MILIWAT, MILI_AMP, VOLT } from "@nms-ng2/app/shared/constants/units";
import { PopoverHelperService } from "../../diagram/popover/popover-helper.service";
import { ANGULARJS_TRANSLATE } from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";
import { TranslatedText } from "@nms-ng2/app/shared/models/translation.models";

/**
 * Responsável por converter @see OscsConverterData em @see OscElement ou @see OscPositionMap.
 */
@Injectable({
    providedIn: "root"
})
export class DwdmDiagramDiagnosticConverterOscsService extends DwdmDiagramAbstractConverterOscs {

    private readonly translatedDiagramCardField;

    constructor(private readonly buildElementPopoverService: PopoverDataService,
        private readonly popoverVisibilityManagerService: PopoverVisibilityManagerService,
        private readonly popoverHelperService: PopoverHelperService,
        @Inject(ANGULARJS_TRANSLATE) private readonly translate) {
        super();
        this.translatedDiagramCardField = {
            TEMPERATURE: translate.instant("dwdm.circuit.modal.diagnostic.table.temperature"),
            VOLTAGE: translate.instant("dwdm.circuit.modal.diagnostic.table.voltage"),
            CURRENT: translate.instant("dwdm.circuit.modal.diagnostic.table.current"),
            TX_POWER: translate.instant("dwdm.circuit.modal.diagnostic.table.txPower"),
            RX_POWER: translate.instant("dwdm.circuit.modal.diagnostic.table.rxPower"),
            LINK_STATUS: translate.instant("dwdm.circuit.modal.diagnostic.table.status")
        };
    }

    /**
     * Converte a OSC de um END.
     * ENDs só podem ter uma OSC.
     */
    public convertForEnd(oscsData: OscsConverterData, dwdmCircuitDiagnosticData: DwdmCircuitDiagnosticData,
            diagnosticModalViewRef: ViewContainerRef): OscElement | null {
        return super.convertDataForEnd(oscsData, (oscElement: OscElement, osc: OscManagementData) => {
            oscElement.popoverData = this.getPopoverData(dwdmCircuitDiagnosticData, osc);
            oscElement.buildPopover = this.getPopoverBuilder(oscElement, diagnosticModalViewRef);
        });
    }

    /**
    * Converte as OSCs de um Intermediário.
    * Intermediários podem ter até 2 OSCs.
    */
    public convertForIntermediate(oscsData: OscsConverterData, dwdmCircuitDiagnosticData: DwdmCircuitDiagnosticData,
        diagnosticModalViewRef: ViewContainerRef): OscPositionMap {

        return super.convertDataForIntermediate(oscsData, (oscElement: OscElement, osc: OscManagementData) => {
            oscElement.popoverData = this.getPopoverData(dwdmCircuitDiagnosticData, osc);
            oscElement.buildPopover = (graph: Graph, cell: CustomCell) => {
                const target = this.buildElementPopoverService.create(graph, cell, cell.popoverData);

                this.popoverVisibilityManagerService.show(diagnosticModalViewRef, target)
            }
        });
    }

    private getPopoverData(dwdmCircuitDiagnosticData: DwdmCircuitDiagnosticData, osc: OscManagementData): DiagramCardData {
        const data = dwdmCircuitDiagnosticData.oscsManagement.find(oscManagementData =>
            oscManagementData.circuitPortIdentifierData.deviceResourceId === osc.endResourceId
            && oscManagementData.circuitPortIdentifierData.portNo.toString() === osc.port
        );

        const cardData = new DiagramCardData();
        cardData.title = this.buildTitle(data);
        cardData.informationBlocks = this.buildInformationBlocks(data);
        cardData.alarms = this.buildAlarms(data);

        return cardData;
    }

    private buildTitle(data: PortDiagnosticData): string {
        const { portModelIdentifier, chassisIdentifier, slotIdentifier, portNo } = data.circuitPortIdentifierData;

        return `${portModelIdentifier} ${chassisIdentifier}/${slotIdentifier}/${portNo}`;
    }

    private buildInformationBlocks(data: PortDiagnosticData): DiagramCardInformation[] {
        const transceiverDiagnosticData: TransceiverDiagnosticData = data.transceiverDiagnosticData
            || {} as TransceiverDiagnosticData;

        const temperature = this.popoverHelperService.getTextOrPlaceHolder(transceiverDiagnosticData.temperature, CELSIUS);
        const voltage = this.popoverHelperService.getTextOrPlaceHolder(transceiverDiagnosticData.voltage, VOLT);
        const current = this.popoverHelperService.getTextOrPlaceHolder(transceiverDiagnosticData.current, MILI_AMP);
        const txPower = this.popoverHelperService.getTextOrPlaceHolder(transceiverDiagnosticData.txPower, DECIBEL_MILIWAT);
        const rxPower = this.popoverHelperService.getTextOrPlaceHolder(transceiverDiagnosticData.rxPower, DECIBEL_MILIWAT);
        const linkStatus = this.getLinkStatusText(data.linkStatus);

        return [
            {
                [this.translatedDiagramCardField.TEMPERATURE]: temperature,
                [this.translatedDiagramCardField.VOLTAGE]: voltage,
                [this.translatedDiagramCardField.CURRENT]: current,
                [this.translatedDiagramCardField.TX_POWER]: txPower,
                [this.translatedDiagramCardField.RX_POWER]: rxPower,
                [this.translatedDiagramCardField.LINK_STATUS]: linkStatus
            }
        ];
    }

    private getLinkStatusText(status: string): TranslatedText {
        if (!status) {
            return "N/A";
        }

        const translationKey = LINKS_STATUS_TRANSLATION_MAP[LinkStatus[status.trim().toUpperCase()]];
        return this.translate.instant(translationKey);
    }

    private buildAlarms(data: PortDiagnosticData) {
        return data.alarmsDiagnosticData?.map(alarm => {
            const { information, severity } = alarm;
            const cardSeverity = this.popoverHelperService.normalizeCardSeverity(severity) as AlarmSeverity;

            return { severity: cardSeverity, message: information };
        });
    }

    private getPopoverBuilder(oscElement: OscElement, diagnosticModalViewRef: ViewContainerRef) {
        return (graph: Graph, cell: CustomCell) => {
            const target = this.buildElementPopoverService.create(graph, cell, cell.popoverData);

            this.popoverVisibilityManagerService.show(diagnosticModalViewRef, target);
        };
    }

}
